import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  Bars3Icon,
  CheckIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  checkLightningRecordStatus,
  createLightningRecord,
  deleteLightningRecord,
  downloadS3Document,
  getAllLightningRecord,
  getLightningRecordById,
} from 'services'
import { Button, Modal } from 'stories/components'
import { confirm, formatTime } from 'utils'

import { LightningDocsStatus } from './constants'

export default function LightningDocs({ hasOverview = true }: { hasOverview?: boolean }) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Array<Record<string, any>>>([])
  const [tableData, setTableData] = useState<Record<string, any>>({})
  const [detailData, setDetailData] = useState<Record<string, any>>({})
  const [overrideValues, setOverrideValues] = useState<Record<string, string>>({})
  const [editingItems, setEditingItems] = useState<Set<string>>(new Set())

  const initData = async () => {
    setLoading(true)
    const { records, tableData, overrideValues: savedOverrides } = await getAllLightningRecord()
    setData(records)
    setTableData(tableData)

    // Try to load from localStorage first, then fall back to saved overrides
    try {
      if (savedOverrides && Object.keys(savedOverrides).length > 0) {
        setOverrideValues(savedOverrides)
      }
    } catch (e) {
      console.log('Failed to load override values from storage', e)
      if (savedOverrides && Object.keys(savedOverrides).length > 0) {
        setOverrideValues(savedOverrides)
      }
    }

    setLoading(false)
  }

  useEffect(() => {
    initData()
  }, [])

  const onAdd = async (isTest: boolean = false) => {
    setLoading(true)
    const res = await createLightningRecord(isTest, overrideValues)
    if (res.success && res.data) {
      const temp = [res.data, ...data]
      setData(temp)
      toast('Successfully created Lightning Docs Request', { type: 'success' })
    }
    setLoading(false)
  }

  const onDetail = async (recordId: string) => {
    setLoading(true)
    const res = await getLightningRecordById(recordId)
    if (res?.tableData) {
      setDetailData(res.tableData)
    }
    setLoading(false)
  }

  const onDelete = async (recordId: string) => {
    const isContinue = await confirm('Are you sure you want to delete this record?')
    if (!isContinue) return
    setLoading(true)
    const res = await deleteLightningRecord(recordId)
    if (res.success) {
      const temp = data.filter((item) => item.recordId !== recordId)
      setData(temp)
      toast('Successfully deleted!', { type: 'info' })
    }
    setLoading(false)
  }

  const checkStatus = async (recordId: string) => {
    setLoading(true)
    const { app, files } = await checkLightningRecordStatus(recordId)
    const temp = cloneDeep(data)
    const existing = temp.find((item) => item.recordId === recordId)
    if (!existing) return
    existing.apps = [app]
    existing.status = ['Ok', 'Completed'].includes(app.status)
      ? LightningDocsStatus.COMPLETED
      : LightningDocsStatus.PENDING
    existing.files = files
    setData(temp)
    setLoading(false)
  }

  const dowloadFiles = async (files: Record<string, any>[]) => {
    files.map((item: any) => {
      downloadS3Document(item.fileKey, item.name)
    })
  }

  const handleKeyTitle = (title: string, category: string) => {
    if (title.startsWith('Borrower') && title.endsWith('Name')) return title.replace('Borrower', 'Borrower / Signer')
    if (category === 'Lender Information' && title === 'Name') return 'Name of Lender'
    return title
  }

  const handleOverrideChange = (keyPath: string, value: string) => {
    const newOverrides = {
      ...overrideValues,
      [keyPath]: value,
    }
    setOverrideValues(newOverrides)
  }

  const getOverrideValue = (keyPath: string) => {
    return overrideValues[keyPath] || ''
  }

  const clearOverrides = () => {
    setOverrideValues({})
    setEditingItems(new Set())
    toast('Override values cleared', { type: 'info' })
  }

  const toggleEdit = (keyPath: string) => {
    const newEditingItems = new Set(editingItems)
    if (newEditingItems.has(keyPath)) {
      newEditingItems.delete(keyPath)
    } else {
      newEditingItems.add(keyPath)
    }
    setEditingItems(newEditingItems)
  }

  const isEditing = (keyPath: string) => {
    return editingItems.has(keyPath)
  }

  let globalIdx = 1

  const renderTable = (tableData: Record<string, any>, showOverride: boolean = false) => {
    return (
      <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6 mb-2 rounded-md">
        <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th scope="col" className="px-3 py-3">
              Category
            </th>
            <th scope="col" className="px-3 py-3 min-w-[200px]">
              Field
            </th>
            <th scope="col" className="px-3 py-3">
              Value
            </th>
            {showOverride && (
              <th scope="col" className="px-3 py-3 min-w-[200px]">
                Override Value
              </th>
            )}
            <th scope="col" className="px-3 py-3">
              Status
            </th>
            {showOverride && (
              <th scope="col" className="px-3 py-3">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="text-[14px] text-gray-900">
          {Object.keys(tableData)
            .sort()
            .map((category, index: number) => {
              const item = tableData[category]
              const count = Object.keys(item).length
              return (
                <>
                  {Object.keys(item).map((key, subIndex: number) => {
                    const fieldData = item[key]
                    const keyPath = fieldData.keyPath || ''
                    const overrideValue = getOverrideValue(keyPath)
                    const hasOverride = overrideValue !== ''
                    return (
                      <tr key={`${index}-${subIndex}`} className={`border ${index % 2 ? 'bg-gray-50' : ''}`}>
                        {!subIndex && (
                          <td className="px-3 py-2" rowSpan={count}>
                            {category}
                          </td>
                        )}
                        <td className="px-3 py-2">
                          {globalIdx++}. {handleKeyTitle(key, category)}
                        </td>
                        <td className="px-3 py-2">{fieldData.value}</td>
                        {showOverride && (
                          <td className="px-3 py-2">
                            {isEditing(keyPath) ? (
                              <input
                                type="text"
                                className="w-full px-2 py-1 border border-gray-300 rounded"
                                value={overrideValue}
                                onChange={(e) => handleOverrideChange(keyPath, e.target.value)}
                                autoFocus
                              />
                            ) : (
                              <span className="text-gray-500">{overrideValue || '-'}</span>
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2 flex items-center">
                          {(hasOverride && showOverride) || fieldData.status ? (
                            <span className="ml-1 mb-0.5 text-shade-blue" key={0}>
                              <CheckIcon className="w-4 h-4"></CheckIcon>
                            </span>
                          ) : (
                            <span className="ml-1 mb-0.5 text-red-800" key={0}>
                              <XMarkIcon className="w-4 h-4"></XMarkIcon>
                            </span>
                          )}
                        </td>
                        {showOverride && (
                          <td className="px-3 py-2">
                            <div className="flex">
                              <span
                                className="cursor-pointer hover-shadow1 p-1 text-shade-blue"
                                onClick={() => toggleEdit(keyPath)}
                                title={isEditing(keyPath) ? 'Close' : 'Edit'}
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </span>
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </>
              )
            })}
        </tbody>
      </table>
    )
  }

  return (
    <div className="py-6 px-2">
      {hasOverview && <Overview title="Lightning Docs" />}
      <div className="max-w-screen-2xl m-auto">
        <LayoutLoading show={loading} />
        <div className={`relative bg-white rounded mb-4 ${hasOverview ? 'shadow1 md:p-6 p-2' : ''}`}>
          {renderTable(tableData, true)}
          <div className="text-right flex justify-between items-center">
            <div>
              {Object.keys(overrideValues).length > 0 && (
                <Button color="red" onClick={clearOverrides}>
                  {`Clear Overrides (${Object.keys(overrideValues).filter((k) => overrideValues[k]).length})`}
                </Button>
              )}
            </div>
            <div>
              <Button color="gray" onClick={() => onAdd(true)}>
                Sample Docs
              </Button>
              <Button onClick={() => onAdd(false)}>Order Docs</Button>
            </div>
          </div>
          <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-3 py-3">
                  No
                </th>
                <th scope="col" className="px-3 py-3">
                  Is Test File?
                </th>
                <th scope="col" className="px-3 py-3">
                  Created By
                </th>
                <th scope="col" className="px-3 py-3">
                  Created At
                </th>
                <th scope="col" className="px-3 py-3">
                  Status
                </th>
                <th scope="col" className="px-3 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="text-[14px] text-gray-900">
              {data.map((item, index: number) => {
                return (
                  <tr key={item.id}>
                    <td className="px-3 py-3">{index + 1}</td>
                    <td className="px-3 py-3">{item.data?.isTestFile ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-3">{item.email}</td>
                    <td className="px-3 py-3">{formatTime(item.createdAt)}</td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2 items-center">
                        <span
                          className={`p-1 min-w-[100px] text-center ${
                            item.status === LightningDocsStatus.COMPLETED ? 'bg-green-300' : 'bg-gray-300'
                          } rounded-md`}
                        >
                          {item.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 flex gap-2 items-center">
                      <div className="flex gap-2 items-center">
                        {item.status === LightningDocsStatus.COMPLETED && item.files?.length ? (
                          <span
                            className="p-1 hover-shadow1 cursor-pointer text-shade-blue"
                            onClick={() => dowloadFiles(item.files)}
                          >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="cursor-pointer hover-shadow1 p-1" onClick={() => checkStatus(item.recordId)}>
                            <ArrowPathIcon className="w-4 h-4" />
                          </span>
                        )}
                        <span
                          className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                          onClick={() => onDetail(item.recordId)}
                        >
                          <Bars3Icon className="w-4 h-4" />
                        </span>
                        <span
                          className="text-red-800 p-1 hover-shadow1 cursor-pointer"
                          onClick={() => onDelete(item.recordId)}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      {Object.keys(detailData).length > 0 && (
        <Modal
          title="Lightning Docs Details"
          isOpen={true}
          onClose={() => setDetailData({})}
          onOk={() => setDetailData({})}
          titleOkay="Close"
          titleCancel="NONE"
        >
          <div className="max-h-[70vh] overflow-y-auto">{renderTable(detailData, false)}</div>
        </Modal>
      )}
    </div>
  )
}
